import { useEffect, useTransition } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import {
  errors,
  IApiError,
  isTranslatableItem,
  Log,
  normalizeErrors,
  transformApiCrudErrorForToast,
} from "@ryde/shared";

import { useLandingApi } from "../api";
import { landingInitDataAtom } from "../atoms";
import { useLandingsNavigation } from "./use-landings-navigation";

export const useLandingInit = ({ companyId }: { companyId: number }) => {
  const { t } = useTranslation();
  const { navigatePageNotFound } = useLandingsNavigation();
  const [landingInitData, setLandingInitData] =
    useRecoilState(landingInitDataAtom);
  const [isLoading, setTransition] = useTransition();
  const { getLandingInitData } = useLandingApi();

  useEffect(() => {
    const fetchLandingInitData = async (
      landingCompanyId: number,
    ): Promise<void> => {
      try {
        const landingInitDataResult = await getLandingInitData({
          companyId: landingCompanyId,
        });

        if (landingInitDataResult?.success && landingInitDataResult.data) {
          setLandingInitData(landingInitDataResult.data);
        } else {
          if (isTranslatableItem(landingInitDataResult?.error)) {
            Log.warnAndToast(landingInitDataResult.error);
          } else {
            Log.warnAndToast(errors.landing.loadingFailed);
          }

          setLandingInitData(undefined);
          navigatePageNotFound();
        }
      } catch (error) {
        const errorsNormalized = normalizeErrors(error as IApiError, t);
        Log.errorAndToast(
          transformApiCrudErrorForToast({ errorsNormalized, tFunction: t }),
        );
        navigatePageNotFound();
      }
    };

    setTransition(() => {
      fetchLandingInitData(companyId);
    });
  }, [
    companyId,
    getLandingInitData,
    navigatePageNotFound,
    setLandingInitData,
    t,
  ]);
  return { landingInitData, isLoading };
};
